import { Injectable } from '@angular/core';
import { PendingUndoableOperation } from './PendingUndoableOperation';

export interface RunningOperation {
  completionPromise: Promise<any>;
  pendingUndo?: PendingUndoableOperation<any>;
}

interface UndoComponentState {
  pendingUndoableOperation: PendingUndoableOperation<any> | null;
  runningOperation: boolean;
  queuedOperations: Array<(skipUndo: boolean) => PromiseLike<RunningOperation>>;
  abandoned: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ApUndoableOperationService {
  private componentStates = new Map<number, UndoComponentState>();
  private nextComponentId = 1;

  registerComponent() {
    const nextId = this.nextComponentId;
    this.nextComponentId += 1;
    this.componentStates.set(nextId, {
      pendingUndoableOperation: null,
      runningOperation: false,
      queuedOperations: [],
      abandoned: false,
    });
    return nextId;
  }

  unregisterComponent(undoComponentId: number) {
    const componentState = this.componentStates.get(undoComponentId);
    this.componentStates.delete(undoComponentId);
    if (componentState) {
      componentState.abandoned = true;
    }
  }

  queueOperationAsync(undoComponentId: number, operation: (skipUndo: boolean) => PromiseLike<RunningOperation>) {
    const componentState = this.componentStates.get(undoComponentId);
    if (!componentState) {
      throw new Error(`Invalid component id ${undoComponentId}`);
    }

    const runningOperation = operation;
    if (!componentState.runningOperation) {
      this.runOperation(componentState, runningOperation);
    } else {
      if (componentState.pendingUndoableOperation) {
        componentState.pendingUndoableOperation.commit();
        componentState.pendingUndoableOperation = null;
      }

      componentState.queuedOperations.push(runningOperation);
    }
  }

  queueOperation(undoComponentId: number, operation: (skipUndo: boolean) => RunningOperation) {
    this.queueOperationAsync(undoComponentId, (skipUndo) => Promise.resolve(operation(skipUndo)));
  }

  private dequeueOperation(componentState: UndoComponentState, ) {
    const nextOperation = componentState.queuedOperations.shift();
    if (!nextOperation) {
      componentState.runningOperation = false;
    } else {
      this.runOperation(componentState, nextOperation);
    }
  }

  private async runOperation(componentState: UndoComponentState, operation: (skipUndo: boolean) => PromiseLike<RunningOperation>) {
    if (componentState.pendingUndoableOperation) {
      componentState.pendingUndoableOperation.commit();
      componentState.pendingUndoableOperation = null;
    }

    componentState.runningOperation = true;
    const skipUndo = componentState.queuedOperations.length > 0;
    const result = await operation(skipUndo);
    if (result.pendingUndo) {
      componentState.pendingUndoableOperation = result.pendingUndo;
    }

    result.completionPromise.finally(() => {
      if (componentState.pendingUndoableOperation === result.pendingUndo) {
        componentState.pendingUndoableOperation = null;
      }

      if (!componentState.abandoned) {
        this.dequeueOperation(componentState);
      }
    });
  }
}
